import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

interface EntregadorChamadoPayload {
  nomeEntregador?: string;
  status?: string;
  lojaId?: number;
}

export const useEntregadorChamado = (
  onChamado: (nome: string, sobrenome: string) => void,
  lojaId?: number | null
) => {
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  const connect = useCallback(() => {
    if (!token) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);
    const url = `${apiUrl}/events/ranking/entrega?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
    });

    eventSource.addEventListener(
      "entregador.chamado",
      (event: MessageEvent) => {
        try {
          const payload: EntregadorChamadoPayload = JSON.parse(event.data);
          if (payload.nomeEntregador) {
            const nomeCompleto = payload.nomeEntregador.split(" ");
            const nome = nomeCompleto[0] || "";
            const sobrenome = nomeCompleto.slice(1).join(" ") || "";
            onChamado(nome, sobrenome);
          }
        } catch (error) {
          console.error("Erro ao processar evento de chamada:", error);
        }
      }
    );

    eventSource.onerror = (error) => {
      console.error("SSE error for entregador chamado:", error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        console.error(
          "Max reconnection attempts reached for entregador chamado"
        );
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, lojaId, onChamado]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);
};
